import { createFileRoute, Link } from "@tanstack/react-router";
import { AcademyNav } from "@/components/academy/AcademyNav";
import { Users, MessageCircle, Sparkles } from "lucide-react";
import { ACADEMY_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Academy Community — Study Together | Buddha KA";
const DESCRIPTION =
  "Study with fellow practitioners inside the Buddha KA Academy — circles, discussions, and shared reflections across every domain of the canon.";

export const Route = createFileRoute("/academy/community")({
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/academy/community" }),
    scripts: [
      ldScript(
        pageNode({
          type: "WebPage",
          id: "https://buddhaka.com/academy/community#community",
          url: "https://buddhaka.com/academy/community",
          name: TITLE,
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: { isPartOf: { "@id": ACADEMY_ID } },
        }),
      ),
      ldScript(breadcrumbs([["Academy", "/academy"], ["Community", "/academy/community"]])),
    ],
  }),
  component: CommunityPage,
});


function CommunityPage() {
  return (
    <>
      <AcademyNav />
      <div className="min-h-dvh bg-obsidian text-foreground">
        <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2"><Users size={14}/> Community</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-gold-luminous">Study together.</h1>
          <p className="mt-4 max-w-2xl text-lg text-foreground/75">
            The Academy is a living current. Join circles, ask the collective, and share what you're learning.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            <Link to="/circles" className="rounded-2xl border hairline p-6 hover:border-gold/60">
              <div className="flex items-center gap-2 text-gold"><Users size={18} /><span className="text-xs uppercase tracking-widest">Circles</span></div>
              <h2 className="mt-3 font-serif text-2xl">Join a Circle</h2>
              <p className="mt-2 text-sm text-foreground/70">Small groups practicing the same domain together — Human Design, energy mechanics, and beyond.</p>
            </Link>
            <Link to="/ask" className="rounded-2xl border hairline p-6 hover:border-gold/60">
              <div className="flex items-center gap-2 text-gold"><MessageCircle size={18} /><span className="text-xs uppercase tracking-widest">Ask</span></div>
              <h2 className="mt-3 font-serif text-2xl">Ask Buddha KA</h2>
              <p className="mt-2 text-sm text-foreground/70">Public conversations with the canon — question, reflection, transmission.</p>
            </Link>
            <Link to="/wisdom" className="rounded-2xl border hairline p-6 hover:border-gold/60">
              <div className="flex items-center gap-2 text-gold"><Sparkles size={18} /><span className="text-xs uppercase tracking-widest">Wisdom</span></div>
              <h2 className="mt-3 font-serif text-2xl">Read the Wisdom Feed</h2>
              <p className="mt-2 text-sm text-foreground/70">Reflections from the field, updated with every activation.</p>
            </Link>
            <Link to="/academy/journal" className="rounded-2xl border hairline p-6 hover:border-gold/60">
              <div className="flex items-center gap-2 text-gold"><span className="text-xs uppercase tracking-widest">Private</span></div>
              <h2 className="mt-3 font-serif text-2xl">Your Journal</h2>
              <p className="mt-2 text-sm text-foreground/70">Not everything belongs in public. Keep your own reflections private.</p>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
